// Storefront presentation only. Shopify remains the authority for prices, variants and carts.
(() => {
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const hoverNavigation = matchMedia('(min-width:900px) and (hover:hover)');
  document.addEventListener('click', event => {
    const target = event.target.closest('button');
    if (!target) return;
    if (target.matches('.fp-mega-trigger,.fp-desktop-menu')) {
      const panel = document.getElementById(target.getAttribute('aria-controls'));
      // A mouse click must not immediately close a menu that mouseenter opened.
      const hoverClick = hoverNavigation.matches && event.detail > 0 && event.pointerType !== 'touch';
      const shouldOpen = hoverClick || panel.hidden;
      closeMega();
      panel.hidden = !shouldOpen;
      target.setAttribute('aria-expanded', String(!panel.hidden));
    }
    if (target.matches('[data-fp-menu-open]')) {
      const dialog = document.getElementById(target.getAttribute('aria-controls'));
      dialog.showModal(); target.setAttribute('aria-expanded', 'true');
      dialog.addEventListener('close', () => { target.setAttribute('aria-expanded', 'false'); target.focus(); }, { once: true });
    }
    if (target.matches('[data-fp-dialog-close]')) target.closest('dialog').close();
    if (target.matches('[data-fp-estimator-open]')) document.querySelector('[data-fp-estimator]')?.showModal();
  });
  function activateTabGroup(group, tab) {
    group.querySelectorAll('[data-fp-tab]').forEach(button => {
      const active = button === tab;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
    group.querySelectorAll('[data-fp-panel]').forEach(panel => { panel.hidden = panel.dataset.fpPanel !== tab.dataset.fpTab; });
  }
  function connect(root = document) {
    root.querySelectorAll('.fp-mood-header:not([data-menu-connected])').forEach(header => {
      header.dataset.menuConnected = 'true';
      const navigation = header.querySelector('.fp-mood-nav');
      // A's shorter nav sits inside a taller bar: include the space below it.
      const hoverRegions = [header.matches('[data-atelier-header]') ? header : navigation];
      let closeTimer;
      const cancelClose = () => clearTimeout(closeTimer);
      const scheduleClose = () => {
        cancelClose();
        // Allow a diagonal move past a neighbouring link into A's dropdown.
        if (header.matches('[data-atelier-header]')) closeTimer = setTimeout(() => closeMega(), 200);
        else closeMega();
      };
      header.addEventListener('click', cancelClose);
      header.addEventListener('focusin', cancelClose);
      header.querySelectorAll('.fp-mega-trigger,.fp-desktop-menu').forEach(trigger => {
        const panel = document.getElementById(trigger.getAttribute('aria-controls'));
        hoverRegions.push(panel);
        trigger.addEventListener('mouseenter', () => {
          if (!hoverNavigation.matches) return;
          cancelClose();
          closeMega();
          panel.hidden = false;
          trigger.setAttribute('aria-expanded', 'true');
        });
      });
      // Keep the trigger, intervening header space and dropdown one hover region.
      hoverRegions.forEach(region => {
        region.addEventListener('mouseenter', cancelClose);
        region.addEventListener('mouseleave', event => {
          if (!hoverNavigation.matches) return;
          const next = event.relatedTarget;
          if (next instanceof Node && hoverRegions.some(other => other.contains(next))) return;
          scheduleClose();
        });
      });
      navigation.querySelectorAll('a').forEach(link => link.addEventListener('mouseenter', () => {
        if (hoverNavigation.matches) scheduleClose();
      }));
      header.addEventListener('focusout', event => {
        if (event.relatedTarget && !header.contains(event.relatedTarget)) closeMega();
      });
    });
    root.querySelectorAll('[data-fp-tabs]:not([data-connected]),.fp-mega__panel:not([data-connected])').forEach(group => {
      group.dataset.connected = 'true';
      const tabs = Array.from(group.querySelectorAll('[data-fp-tab]'));
      let hoverTimer;
      const cancelHover = () => clearTimeout(hoverTimer);
      const activate = tab => { cancelHover(); activateTabGroup(group, tab); };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => activate(tab));
        if (group.matches('.fp-mega__panel')) {
          // Let the pointer cross another row on its way to the open panel.
          tab.addEventListener('mouseenter', () => {
            cancelHover();
            if (hoverNavigation.matches) hoverTimer = setTimeout(() => { if (!group.hidden) activate(tab); }, 120);
          });
          tab.addEventListener('mouseleave', cancelHover);
        }
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index + tabs.length - 1) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); activate(tabs[next]); tabs[next].focus(); }
        });
      });
    });
    root.querySelectorAll('[data-fp-carousel]:not([data-connected])').forEach(carousel => {
      carousel.dataset.connected = 'true';
      const track = carousel.querySelector('[data-fp-carousel-track]');
      if (!track) return;
      const prev = carousel.querySelector('[data-fp-carousel-prev]');
      const next = carousel.querySelector('[data-fp-carousel-next]');
      const update = () => { prev.disabled = track.scrollLeft < 2; next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2; };
      prev.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth, behavior: reduceMotion.matches ? 'instant' : 'smooth' }));
      next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth, behavior: reduceMotion.matches ? 'instant' : 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      const resize = new ResizeObserver(update); resize.observe(track); update();
    });
    root.querySelectorAll('[data-fp-hero]:not([data-connected])').forEach(hero => {
      hero.dataset.connected = 'true';
      const slides = Array.from(hero.querySelectorAll('[data-fp-hero-slide-v2]'));
      if (slides.length < 2) return;
      const dots = Array.from(hero.querySelectorAll('[data-fp-hero-dot]'));
      const pause = hero.querySelector('[data-fp-hero-pause]');
      let current = 0, paused = reduceMotion.matches, visible = true;
      function show(index) { current = index; slides.forEach((slide, i) => { slide.hidden = i !== index; }); dots.forEach((dot, i) => dot.setAttribute('aria-pressed', String(i === index))); }
      function syncPause() { pause.setAttribute('aria-pressed', String(paused)); pause.setAttribute('aria-label', paused ? window.FivePointI18n.t("Play slideshow") : window.FivePointI18n.t("Pause slideshow")); pause.textContent = paused ? '▷' : 'Ⅱ'; }
      dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); paused = true; syncPause(); }));
      pause.addEventListener('click', () => { paused = !paused; syncPause(); });
      hero.addEventListener('focusin', () => { paused = true; syncPause(); });
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; }); observer.observe(hero);
      const timer = setInterval(() => { if (!hero.isConnected) { clearInterval(timer); observer.disconnect(); return; } if (!paused && visible && !document.hidden) show((current + 1) % slides.length); }, 10000);
      syncPause();
    });
    root.querySelectorAll('[data-fp-estimator-form]:not([data-connected])').forEach(form => {
      form.dataset.connected = 'true';
      form.addEventListener('submit', event => {
        event.preventDefault();
        if (!form.reportValidity()) return;
        const values = new FormData(form);
        const metres = Math.ceil(Number(values.get('length')) * (1 + Number(values.get('allowance')) / 100));
        form.querySelector('[data-fp-estimator-result]').textContent = window.FivePointI18n.t("Estimated fabric: {{p0}} metre{{p1}}", [metres, metres === 1 ? '' : 's']);
      });
    });
    window.FivePointSavedControls?.render();
  }
  function closeMega(focusTrigger = false) {
    document.querySelectorAll('.fp-mega-trigger[aria-expanded="true"],.fp-desktop-menu[aria-expanded="true"]').forEach(trigger => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      panel.hidden = true;
      const firstTab = panel.querySelector('[data-fp-tab]');
      if (firstTab) activateTabGroup(panel, firstTab);
      trigger.setAttribute('aria-expanded', 'false');
      if (focusTrigger) trigger.focus();
    });
  }
  document.addEventListener('keydown', event => { if (event.key === 'Escape') closeMega(true); });
  document.addEventListener('click', event => { if (!event.target.closest('.fp-mega-trigger,.fp-mega__panel,.fp-desktop-menu,.fp-discover-panel')) closeMega(); });
  matchMedia('(min-width:900px)').addEventListener('change', () => closeMega());
  document.addEventListener('atelier:catalog-updated', () => {
    window.FivePointSavedControls?.render();
  });
  document.addEventListener('shopify:section:load', event => connect(event.target));
  connect();
})();
